import secureApiService from './secureApiService'
import { sanitizeInput } from '@/utils/security'
import { sanitizeFormData } from '@/utils/formSecurity'
import {
  StartAdmissionResponse,
  VerifyAdmissionResponse,
  CreateApplicationRequest,
  CreateApplicationResponse,
  CreateApplicationResponseData,
  AdmissionStatusResponseJSON,
} from '@/types/admissions'
import { ApplicationModel } from '@/models/application.model'
import { PaginatedResponse } from '@/models/templates/pagination.model'

class AdmissionsService {
  private admissionsBase = '/admissions'

  async startAdmission(email: string, purpose: string = 'application'): Promise<StartAdmissionResponse> {
    const payload = {
      email: sanitizeInput(email),
      purpose: sanitizeInput(purpose),
    }

    const response = await secureApiService.post<StartAdmissionResponse>(
      `${this.admissionsBase}/start`,
      payload,
      { requireAuth: false, skipCSRF: true, timeout: 150000 },
    )

    const data = response.data

    if (!data?.success) {
      throw new Error(data?.message || 'Unable to start admission process')
    }

    return data
  }

  async verifyAdmission(
    sessionId: string,
    code: string,
    programId?: string,
    purpose: string = 'application',
  ): Promise<VerifyAdmissionResponse> {
    const payload: Record<string, string> = {
      sessionId: sanitizeInput(sessionId),
      code: sanitizeInput(code),
      purpose: sanitizeInput(purpose),
    }

    if (programId) {
      payload.programId = sanitizeInput(programId)
    }

    const response = await secureApiService.post<VerifyAdmissionResponse>(
      `${this.admissionsBase}/verify`,
      payload,
      { requireAuth: false, skipCSRF: true },
    )

    const data = response.data

    if (!data?.success) {
      throw new Error(data?.message || 'Unable to verify admission code')
    }

    return data
  }

  async createApplication(request: CreateApplicationRequest): Promise<CreateApplicationResponseData> {
    const formData = new FormData()
    formData.append('sessionId', sanitizeInput(request.sessionId))

    if (request.applicationId) {
      formData.append('applicationId', sanitizeInput(request.applicationId))
    }

    const sanitizedPayload = sanitizeFormData(request.payload)
    formData.append('payload', JSON.stringify(sanitizedPayload))

    const files = request.files
    if (files?.meansOfIdentification) {
      formData.append('meansOfIdentification', files.meansOfIdentification)
    }
    if (files?.certificateFile) {
      formData.append('certificateFile', files.certificateFile)
    }
    if (files?.resultFile) {
      formData.append('resultFile', files.resultFile)
    }
    if (files?.paymentReceipt) {
      formData.append('paymentReceipt', files.paymentReceipt)
    }
    if (files?.jambResultFile) {
      formData.append('jambResultFile', files.jambResultFile)
    }

    const response = await secureApiService.postFormData<CreateApplicationResponse>(
      `${this.admissionsBase}/applications`,
      formData,
      { requireAuth: false, skipCSRF: true, timeout: 150000 },
    )

    const data = response.data

    if (!data?.success || !data.data) {
      throw new Error(data?.message || 'Unable to submit application')
    }

    return data.data
  }

  async checkStatus(appId: string): Promise<AdmissionStatusResponseJSON['data']> {
    const sanitizedAppId = sanitizeInput(appId)

    const response = await secureApiService.get<AdmissionStatusResponseJSON>(
      `${this.admissionsBase}/applications/status?appId=${encodeURIComponent(sanitizedAppId)}`,
      { requireAuth: false, skipCSRF: true },
    )

    const payload = response.data

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to retrieve admission status')
    }

    return payload.data
  }

  async getApplicationById(id: string): Promise<ApplicationModel> {
    const response = await secureApiService.get<{ success: boolean; data: any, message?: string }>(
      `${this.admissionsBase}/applications/${id}`,
      { requireAuth: true },
    )
    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to retrieve application details')
    }
    return ApplicationModel.fromJSON(payload.data)
  }

  async getApplicationsPage(params: { page?: number, perPage?: number}): Promise<PaginatedResponse<ApplicationModel>> {
    const response = await secureApiService.get<{
      success: boolean;
      message: string;
      meta: { total: number; page: number; perPage: number };
      data: any[];
    }>(
      `${this.admissionsBase}/applications?page=${params.page??1}&perPage=${params.perPage??25}`,
      { requireAuth: true },
    );

    const payload = response.data;

    if (!payload?.success || !Array.isArray(payload.data)) {
      throw new Error(payload?.message || 'Unable to retrieve applications');
    }

    const data = payload.data.map((item) => ApplicationModel.fromJSON(item));
    return { data, meta: payload.meta };
  }

  async updateApplicationStatus(applicationId: string, to: string, note: string) {
    const payload = { to, note }

    const response = await secureApiService.put<{
      success: boolean
      message: string
    }>(
      `${this.admissionsBase}/applications/${applicationId}/status`,
      payload,
      { requireAuth: true },
    )

    const data = response.data
    if (!data?.success) {
      throw new Error(data?.message || 'Unable to update status')
    }

    return data
  }

  async updateApplicationProgramme(
    applicationId: string,
    programmeId: string,
    programmeCohortId?: string | null,
  ) {
    const payload: Record<string, string> = {
      programmeId,
    }

    if (programmeCohortId) {
      payload.programmeCohortId = programmeCohortId
    }

    const response = await secureApiService.put<{
      success: boolean
      message: string
    }>(
      `${this.admissionsBase}/applications/${applicationId}/programme`,
      payload,
      { requireAuth: true },
    )

    const data = response.data
    if (!data?.success) {
      throw new Error(data?.message || 'Unable to update programme')
    }

    return data
  }

}

export const admissionsService = new AdmissionsService()
